<script setup>
import AttachmentFiles from "../../Components/AttachmentFiles.vue";
import {useHttpErrorsHandler} from "../../Composables/httpErrorsHandler.js";
import {useRouter} from "vue-router";
import {computed, inject, onBeforeMount, reactive, ref} from "vue";
import Loader from "../../Components/Loader.vue";
import {useToast} from "vue-toast-notification";
import FormInputFilePond from "../../Components/FormInputFilePond.vue";
import axios from "axios";
import ChiptuningChartsGallery from "./ChiptuningChartsGallery.vue";
import ChiptuningPhotosGallery from "./ChiptuningPhotosGallery.vue";
import ChiptuningInternalNote from "./ChiptuningInternalNote.vue";
import {useChiptuningEdit} from "../../Composables/useChiptuningEdit.js";
import {api as viewerApi} from "v-viewer";
import {useSubscriptionStore} from "../../Store/SubscriptionStore.js";
import {MODULES} from "../../Constants/modules.js";
import {chToKw} from "../../Utils/powerUtils.js";
const $toast = useToast();
const { httpErrorsHandler } = useHttpErrorsHandler();
const router = useRouter();
const brandConfig = inject('brandConfig');
const isShiftech = brandConfig?.brand === 'shiftech';
const subscriptionStore = useSubscriptionStore();

const canViewNotes = computed(() => {
    if (!subscriptionStore.billingEnabled) return true
    return subscriptionStore.hasModuleAccess(MODULES.CHIPTUNING_NOTES)
});
const loading = ref(true)
const errors = ref(null)
const engine = ref(null)
function getCarEngine(carEngineId) {
    loading.value = true;
    axios.get("/api/chiptuning/" + carEngineId)
        .then(response => {
            engine.value = response.data;
            loading.value = false;
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

onBeforeMount(() => {
    getCarEngine(router.currentRoute.value.params.id)
})

const activeTab = ref('stage1Tab')

const percentage = ref(0);
const loadingAttachments = ref(false)
const showUploadAttachments = ref(false)
const resetFileList = ref(false)
const form = reactive({
    attachments: []
})
function addAttachment() {
    loadingAttachments.value = true;
    let formData = new FormData();

    Object.entries(form).forEach(([key, value]) => {
        return Array.from(value).forEach(attachment => {
            formData.append(`${key}[]`, attachment);
        });
    });

    const config = {
        onUploadProgress: progressEvent => percentage.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
    }

    axios.post("/api/chiptuning/" + router.currentRoute.value.params.id +"/attachments", formData, config)
        .then(response => {
            form.attachments = [];
            resetFileList.value = true;
            $toast.success('Devis ajouté avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            engine.value.data.attachments = response.data.data.attachments;
            showUploadAttachments.value = false;
            loadingAttachments.value = false;
            resetFileList.value = false;
        })
        .catch(error => {
            errors.value = error.response.data.errors;
            httpErrorsHandler(error);
            loadingAttachments.value = false;
        });
}

function deleteAttachment(id, index){
    axios.delete("/api/attachments/" + id)
        .then(() => {
            engine.value.data.attachments.splice(index, 1);
            $toast.success('Fichier supprimé avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}
function getCategoryLabel(category) {
    switch (category) {
        case 'engine':
            return "Moteur";
        case 'gearbox':
            return "Boîte de vitesse";
        case 'bms':
            return "BMS";
        default:
            return category;
    }
}
const ecuToShow = ref(null)
function showEcuReadingTools(ecu) {
    ecuToShow.value = ecu;
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalShowReadingTools'))
    modale.show()
}

function sortOptions(options) {
    return options.sort((a, b) => a.name.localeCompare(b.name));
}

function showModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

function getNote(note) {
    if (!note) return '--';
    if (typeof note === 'string') return note;
    if (typeof note === 'object' && note.fr) return note.fr;
    return '--';
}

const previewImage = (url, event) => {
    event?.preventDefault()

    console.log('Image URL:', url)

    viewerApi({
        images: [url],
        options: {
            toolbar: false,
            transition: true,
            loading: true,
            initialViewIndex: 0,
            keyboard: true,
        }
    })
}

/*
 * Édition des données véhicule (push vers Shiftech-API).
 * Réservé aux rôles admin / manager / back-office (engine.can.edit_vehicle_data).
 */
const canEdit = computed(() => engine.value?.can?.edit_vehicle_data === true)
// Édition réservée à Shiftech (cohérent avec les onglets photos/courbes, déjà isShiftech).
const canEditShiftech = computed(() => canEdit.value && isShiftech)
const currentEngineId = computed(() => router.currentRoute.value.params.id)

const { saving: savingEdit, uploadPhotos, uploadDynoCharts, addDatalogLink, updateInternalNote } = useChiptuningEdit({
    onUpdated: (data) => { engine.value = data },
    onError: (error) => {
        errors.value = error.response?.data?.errors ?? null
        httpErrorsHandler(error)
    },
})

// Courbes dyno (moteur) : plafonnées par stage et par marque côté Shiftech-API.
// Doit rester aligné sur EnforcesVehicleMediaLimits de Shiftech-API.
const CHART_STAGES = [
    { value: 'stage1', label: 'Stage 1' },
    { value: 'stage2', label: 'Stage 2' },
    { value: 'stage3', label: 'Stage 3' },
    { value: 'e85', label: 'E85' },
    { value: 'e85plus', label: 'E85+' },
]
const MAX_CHARTS_PER_STAGE = 3

const showChartUpload = ref(false)
const chartFiles = ref([])
const chartStage = ref('stage1')
const resetChartFiles = ref(false)

const currentChartStageCount = computed(
    () => (engine.value?.data?.dyno_charts ?? []).filter((c) => c.stage === chartStage.value).length,
)
const chartStageIsFull = computed(() => currentChartStageCount.value >= MAX_CHARTS_PER_STAGE)
const chartStageRemaining = computed(() => Math.max(MAX_CHARTS_PER_STAGE - currentChartStageCount.value, 0))
// L'envoi est multiple : on refuse aussi une sélection qui ferait dépasser le plafond.
const tooManyCharts = computed(() => chartFiles.value.length > chartStageRemaining.value)

async function submitCharts() {
    if (!chartFiles.value.length || chartStageIsFull.value || tooManyCharts.value) return
    resetChartFiles.value = false
    await uploadDynoCharts(currentEngineId.value, chartFiles.value, chartStage.value)
    chartFiles.value = []
    resetChartFiles.value = true
    showChartUpload.value = false
}

// Photos de réalisations (version)
const showPhotoUpload = ref(false)
const photoFiles = ref([])
const resetPhotoFiles = ref(false)
async function submitPhotos() {
    if (!photoFiles.value.length) return
    resetPhotoFiles.value = false
    await uploadPhotos(currentEngineId.value, photoFiles.value)
    photoFiles.value = []
    resetPhotoFiles.value = true
    showPhotoUpload.value = false
}

// Datalogs (moteur) : des liens uniquement (Autotuner / Tunezilla...), rattachés à un stage.
// Doit rester aligné sur App\Enums\DatalogStageEnum de Shiftech-API.
const DATALOG_STAGES = [
    { value: 'stage1', label: 'Stage 1' },
    { value: 'stage2', label: 'Stage 2' },
    { value: 'stage3', label: 'Stage 3' },
    { value: 'e85', label: 'E85' },
    { value: 'e85plus', label: 'E85+' },
    { value: 'boite', label: 'Boîte' },
    { value: 'origine', label: 'Origine' },
]
const MAX_DATALOGS_PER_STAGE = 5

const showDatalogUpload = ref(false)
const datalogLinkUrl = ref('')
const datalogLinkName = ref('')
const datalogStage = ref('stage1')

const datalogStageLabel = (stage) => DATALOG_STAGES.find((s) => s.value === stage)?.label ?? 'Non classé'

// Reflète côté UI la limite appliquée par l'API (comptée par stage, pour la marque courante).
const currentDatalogStageCount = computed(
    () => (engine.value?.data?.datalogs ?? []).filter((d) => d.stage === datalogStage.value).length,
)
const datalogStageIsFull = computed(() => currentDatalogStageCount.value >= MAX_DATALOGS_PER_STAGE)

// Les datalogs historiques (fichiers) n'ont pas de stage : ils sont regroupés en fin de liste.
const groupedDatalogs = computed(() => {
    const list = engine.value?.data?.datalogs ?? []
    const groups = DATALOG_STAGES
        .map((s) => ({ stage: s.value, label: s.label, items: list.filter((d) => d.stage === s.value) }))
    const unclassified = list.filter((d) => !d.stage)
    if (unclassified.length) {
        groups.push({ stage: null, label: 'Non classé', items: unclassified })
    }
    return groups.filter((g) => g.items.length > 0)
})

async function submitDatalogLink() {
    if (!datalogLinkUrl.value || datalogStageIsFull.value) return
    await addDatalogLink(currentEngineId.value, datalogLinkUrl.value, datalogLinkName.value || null, datalogStage.value)
    datalogLinkUrl.value = ''
    datalogLinkName.value = ''
    showDatalogUpload.value = false
}

// Notes internes par stage
function saveInternalNote(stage, note, done) {
    updateInternalNote(currentEngineId.value, stage, note).then(() => done && done())
}
</script>

<template>
    <div class="row">
        <div class="col-12" v-show="loading">
            <loader />
        </div>
        <div class="col-xl-3" v-if="!loading">
            <div class="card">
                <div class="card-body p-4">
                    <div>
                        <div class="mt-2 mx-auto text-center">
                            <img
                                v-if="!engine.data.thumbnail_url || !isShiftech"
                                :src="engine.data.vehicle_brand.data.logo"
                                alt=""
                                class="avatar-lg"
                            >
                            <img
                                v-else
                                :src="'/format-image/'+engine.data.thumbnail_url+'?format=webp'"
                                class="img-fluid"
                                :alt="engine.data.name"
                                height="225"
                            >
                        </div>
                        <div class="mt-3 text-center">
                            <h4 class="mb-1">{{ engine.data.vehicle_brand_name }}</h4>
                            <p class="text-body fs-15 mb-0">
                                {{ engine.data.vehicle_model_name }}<br>
                                <span class="text-muted fs-13">
                                    {{ engine.data.vehicle_version_name }} - {{ engine.data.name }}
                                </span>
                            </p>

                            <!--Edit buttons-->
                            <ul class="list-inline mb-0 mt-3">
                                <li v-if="isShiftech" class="list-inline-item avatar-xs" :title="engine.data.thumbnail_large_url ? 'Grand thumbnail présent' : 'Grand thumbnail absent'">
                                    <!-- Large Thumbnail Icon -->
                                    <a
                                        v-if="engine.data.thumbnail_large_url"
                                        href="#"
                                       class="avatar-title fs-15 rounded bg-success-subtle text-success"
                                       @click.prevent="previewImage(engine.data.thumbnail_large_url)"
                                    >
                                        <i class="ri-image-2-fill fs-16"></i>
                                    </a>
                                    <!--No Thumbnail-->
                                    <span v-else class="avatar-title fs-15 rounded bg-danger-subtle text-danger">
                                        <i class="ri-image-2-fill fs-16"></i>
                                    </span>
                                </li>
                                <li class="list-inline-item avatar-xs" title="Message alert" v-if="engine.can.update">
                                    <a v-if="engine.data.has_warning"
                                       href="#"
                                       class="avatar-title fs-15 rounded bg-warning-subtle text-warning"
                                       @click.prevent="showModal('modalWarningMessage')">
                                        <i class="ri-alert-fill fs-16"></i>
                                    </a>

                                    <span v-else class="avatar-title fs-15 rounded text-muted bg-body">
                                        <i class="ri-alert-fill fs-16"></i>
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-header align-items-center d-flex"><h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-engine-outline me-2 align-middle fs-4"></i>Info moteur </h4></div>
                <div class="card-body">
                    <div class="table-responsive">
                        <table class="table table-sm table-hover mb-0 table-borderless">
                            <tbody>
                                <tr>
                                    <th><span class="fw-medium">Type de carburant</span></th>
                                    <td>{{ engine.data.power_type_label }}</td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">Moteur</span></th>
                                    <td>{{ engine.data.name }}</td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">Puissance</span></th>
                                    <td>{{ engine.data.horsepower }}<span class="text-muted"> ch</span> <span class="text-muted">({{ chToKw(engine.data.horsepower) }} kW)</span></td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">Gain</span></th>
                                    <td>{{ engine.data.torque }}<span class="text-muted"> Nm</span></td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">Stage 1</span></th>
                                    <td>
                                        <span class="badge fs-11 bg-success-subtle text-success" v-if="engine.data.stage1_available">Oui</span>
                                        <span class="badge fs-11 bg-danger-subtle text-danger" v-else>Non</span>
                                    </td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">Stage 2</span></th>
                                    <td>
                                        <span class="badge fs-11 bg-success-subtle text-success" v-if="engine.data.stage2_available">Oui</span>
                                        <span class="badge fs-11 bg-danger-subtle text-danger" v-else>Non</span>
                                    </td>
                                </tr>

                                <tr>
                                    <th><span class="fw-medium">Stage 3</span></th>
                                    <td>
                                        <span class="badge fs-11 bg-success-subtle text-success" v-if="engine.data.stage3_available">Oui</span>
                                        <span class="badge fs-11 bg-danger-subtle text-danger" v-else>Non</span>
                                    </td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">E85</span></th>
                                    <td>
                                        <span class="badge fs-11 bg-success-subtle text-success" v-if="engine.data.e85_available">Oui</span>
                                        <span class="badge fs-11 bg-danger-subtle text-danger" v-else>Non</span>
                                    </td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">E85+</span></th>
                                    <td>
                                        <span class="badge fs-11 bg-success-subtle text-success" v-if="engine.data.e85plus_available">Oui</span>
                                        <span class="badge fs-11 bg-danger-subtle text-danger" v-else>Non</span>
                                    </td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">Gearbox tune</span></th>
                                    <td>
                                        <span class="badge fs-11 bg-success-subtle text-success" v-if="engine.data.gearbox_tune_available">Oui</span>
                                        <span class="badge fs-11 bg-danger-subtle text-danger" v-else>Non</span>
                                    </td>
                                </tr>
                                <tr>
                                    <th><span class="fw-medium">ECU unlocked</span></th>
                                    <td>
                                        <span class="badge fs-11 bg-success-subtle text-success" v-if="engine.data.ecu_unlocked">Oui</span>
                                        <span class="badge fs-11 bg-danger-subtle text-danger" v-else>Non</span>
                                    </td>
                                </tr>
                                <!--<tr>-->
                                <!--    <th><span class="fw-medium">Création</span></th>-->
                                <!--    <td>{{ engine.data.created_at }}</td>-->
                                <!--</tr>-->
                                <!--<tr>-->
                                <!--    <th><span class="fw-medium">Dernière MAJ</span></th>-->
                                <!--    <td>{{ engine.data.updated_at }}</td>-->
                                <!--</tr>-->
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

        </div>

        <div class="col-xl-9" v-if="!loading">
            <div class="card">
                <div class="card-header">
                    <ul class="nav nav-tabs nav-tabs-custom card-header-tabs border-bottom-0 nav-secondary text-dark" role="tablist">
                        <li class="nav-item" @click.prevent="activeTab = 'stage1Tab'">
                            <a class="nav-link stage1" :class="{ 'active': activeTab === 'stage1Tab' }" data-bs-toggle="tab" href="#stage1Tab" role="tab">
                                Stage 1
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'stage2Tab'">
                            <a class="nav-link stage2" :class="{ 'active': activeTab === 'stage2Tab' }" data-bs-toggle="tab" href="#stage2Tab" role="tab" v-if="engine.data.stage2_available">
                                Stage 2
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'stage3Tab'">
                            <a class="nav-link stage3" :class="{ 'active': activeTab === 'stage3Tab' }" data-bs-toggle="tab" href="#stage3Tab" role="tab" v-if="engine.data.stage3_available">
                                Stage 3
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'e85Tab'">
                            <a class="nav-link e85" :class="{ 'active': activeTab === 'e85Tab' }" data-bs-toggle="tab" href="#e85Tab" role="tab" v-if="engine.data.e85_available">
                                E85
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'e85+Tab'">
                            <a class="nav-link e85plus" :class="{ 'active': activeTab === 'e85+Tab' }" data-bs-toggle="tab" href="#e85+Tab" role="tab" v-if="engine.data.e85plus_available">
                                E85+
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'ecuUnlocked'"  v-if="engine.data.ecu_unlocked">
                            <a class="nav-link stage1" :class="{ 'active': activeTab === 'ecuUnlocked' }" data-bs-toggle="tab" href="#ecuUnlocked" role="tab">
                                ECU unlocked
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'dsgTab'">
                            <a class="nav-link" :class="{ 'active': activeTab === 'dsgTab' }" data-bs-toggle="tab" href="#dsgTab" role="tab" v-if="engine.data.gearbox_tune_available">
                                Gearbox
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'options'">
                            <a class="nav-link options" :class="{ 'active': activeTab === 'options' }" data-bs-toggle="tab" href="#options" role="tab">
                                Options
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'ecus'">
                            <a class="nav-link ecus" :class="{ 'active': activeTab === 'ecus' }" data-bs-toggle="tab" href="#ecus" role="tab">
                                ECUs
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'charts'" v-if="isShiftech">
                            <a class="nav-link" :class="{ 'active': activeTab === 'charts' }" data-bs-toggle="tab" href="#charts" role="tab">
                                Courbes
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'photos'" v-if="isShiftech">
                            <a class="nav-link" :class="{ 'active': activeTab === 'photos' }" data-bs-toggle="tab" href="#photos" role="tab">
                                Photos
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'datalogs'" v-if="canEditShiftech">
                            <a class="nav-link" :class="{ 'active': activeTab === 'datalogs' }" data-bs-toggle="tab" href="#datalogs" role="tab">
                                Datalogs
                            </a>
                        </li>
                        <li class="nav-item" @click.prevent="activeTab = 'quotes'">
                            <a class="nav-link" :class="{ 'active': activeTab === 'quotes' }" data-bs-toggle="tab" href="#quotes" role="tab">
                                Devis
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="card-body">
                    <div class="tab-content">
                        <!--Stage1-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'stage1Tab' }" id="stage1Tab" role="tabpanel">
                            <p class="text-body mb-3">
                                La reprogrammation moteur Stage 1 consiste à optimiser la gestion du moteur pour améliorer les performances de votre véhicule et diminuer la consommation de carburant . C'est l'option parfaite pour une meilleure expérience de conduite sans modifications mécaniques.
                            </p>
                            <div class="table-responsive" v-if="engine.data.stage1_available">
                                <table class="table table-sm table-hover align-middle table-nowrap table-striped">
                                    <thead>
                                    <tr>
                                        <th scope="col" class="col-6"></th>
                                        <th scope="col" class="text-center col-3">PUISSANCE</th>
                                        <th scope="col" class="text-center col-3">COUPLE</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td class="fw-bold">Origine</td>
                                        <td class="text-center text-body">{{ engine.data.horsepower }} ch ({{ chToKw(engine.data.horsepower) }} kW)</td>
                                        <td class="text-center text-body">{{ engine.data.torque }} Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold text-dark">Gain</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;"><span class="stage-1 fw-bold">+</span> {{ engine.data.stage1_horsepower_gain }} ch (+{{ chToKw(engine.data.stage1_horsepower_gain) }} kW)</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;"><span class="stage-1 fw-bold">+</span> {{ engine.data.stage1_torque_gain }}  Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold stage-1">Stage 1</td>
                                        <td class="text-center fw-bolder stage-1">{{ engine.data.stage1_horsepower_total }} ch ({{ chToKw(engine.data.stage1_horsepower_total) }} kW)</td>
                                        <td class="text-center fw-bolder stage-1">{{ engine.data.stage1_torque_total }} Nm</td>
                                    </tr>
                                    </tbody>
                                </table>
                                <p class="fs-15">
                                    <i class="mdi mdi-arrow-right-thin fs-20 align-middle stage-1"></i> <span class=" fw-bold">{{ engine.data.stage1_price }} €</span> <span class="text-muted">TTC</span>
                                </p>
                                <template v-if="canViewNotes">
                                    <hr class="text-muted">
                                    <div class="engineers-notes">
                                        <div class="mt-4 mb-2">
                                            <span class="mb-2">Notes de l'ingénieur :<br></span>
                                            <div class="fst-italic text-body" v-html="getNote(engine.data.stage1_engineers_note)"></div>
                                        </div>
                                    </div>
                                    <hr class="text-muted">
                                    <chiptuning-internal-note
                                        stage="stage1"
                                        :note="engine.data.stage1_internal_note"
                                        :can-edit="canEditShiftech"
                                        :saving="savingEdit"
                                        @save="saveInternalNote"
                                    />
                                </template>
                            </div>
                            <div v-else>
                                <p class="fs-15">
                                    <i class="mdi mdi-arrow-right-thin fs-20 align-middle stage-1"></i> <span class=" fw-bold">Bientôt disponible</span>
                                </p>
                            </div>
                        </div>
                        <!--Stage2-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'stage2Tab' }" id="stage2Tab" role="tabpanel" v-if="engine.data.stage2_available">
                            <p class="text-body mb-3">
                                Le Stage 2 dépasse la simple optimisation logicielle. Il peut être nécessaire d’installer des pièces performances telles qu’un échappement, une admission à haut débit et un échangeur gros volume. Cette préparation est idéale pour les conducteurs en quête d'une augmentation significative de puissance.
                            </p>
                            <div class="table-responsive">
                                <table class="table table-sm table-hover align-middle table-nowrap table-striped">
                                    <thead>
                                    <tr>
                                        <th scope="col" class="col-6"></th>
                                        <th scope="col" class="text-center col-3">PUISSANCE</th>
                                        <th scope="col" class="text-center col-3">COUPLE</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td class="fw-bold">Origine</td>
                                        <td class="text-center text-body">{{ engine.data.horsepower }} ch ({{ chToKw(engine.data.horsepower) }} kW)</td>
                                        <td class="text-center text-body">{{ engine.data.torque }} Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold text-dark">Gain</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;"><span class="stage-2 fw-bold">+</span> {{ engine.data.stage2_horsepower_gain }} ch (+{{ chToKw(engine.data.stage2_horsepower_gain) }} kW)</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;"><span class="stage-2 fw-bold">+</span> {{ engine.data.stage2_torque_gain }}  Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold stage-2">Stage 2</td>
                                        <td class="text-center fw-bolder stage-2">{{ engine.data.stage2_horsepower_total }} ch ({{ chToKw(engine.data.stage2_horsepower_total) }} kW)</td>
                                        <td class="text-center fw-bolder stage-2">{{ engine.data.stage2_torque_total }} Nm</td>
                                    </tr>
                                    </tbody>
                                </table>
                                <p class="fs-15">
                                    <i class="mdi mdi-arrow-right-thin fs-20 align-middle stage-2"></i> <span class=" fw-bold">{{ engine.data.stage2_price }} €</span> <span class="text-muted">TTC</span>
                                </p>
                                <template v-if="canViewNotes">
                                    <hr class="text-muted">
                                    <div class="engineers-notes">
                                        <div class="mt-4 mb-2">
                                            <span class="mb-2">Notes de l'ingénieur :<br></span>
                                            <div class="fst-italic text-body" v-html="getNote(engine.data.stage2_engineers_note)"></div>
                                        </div>
                                    </div>
                                    <hr class="text-muted">
                                    <chiptuning-internal-note
                                        stage="stage2"
                                        :note="engine.data.stage2_internal_note"
                                        :can-edit="canEditShiftech"
                                        :saving="savingEdit"
                                        @save="saveInternalNote"
                                    />
                                </template>
                            </div>
                        </div>
                        <!--Stage3-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'stage3Tab' }" id="stage3Tab" role="tabpanel" v-if="engine.data.stage3_available">
                            <p class="text-body mb-3">
                                Le Stage 3 dépasse la simple optimisation logicielle. Il peut être nécessaire d’installer des pièces performances telles qu’un échappement, une admission à haut débit et un échangeur gros volume. Cette préparation est idéale pour les conducteurs en quête d'une augmentation significative de puissance.
                            </p>
                            <div class="table-responsive">
                                <table class="table table-sm table-hover align-middle table-nowrap table-striped">
                                    <thead>
                                    <tr>
                                        <th scope="col" class="col-6"></th>
                                        <th scope="col" class="text-center col-3">PUISSANCE</th>
                                        <th scope="col" class="text-center col-3">COUPLE</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td class="fw-bold">Origine</td>
                                        <td class="text-center text-body">{{ engine.data.horsepower }} ch ({{ chToKw(engine.data.horsepower) }} kW)</td>
                                        <td class="text-center text-body">{{ engine.data.torque }} Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold text-dark">Gain</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;"><span class="stage-3 fw-bold">+</span> {{ engine.data.stage3_horsepower_gain }} ch (+{{ chToKw(engine.data.stage3_horsepower_gain) }} kW)</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;"><span class="stage-3 fw-bold">+</span> {{ engine.data.stage3_torque_gain }}  Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold stage-3">Stage 2</td>
                                        <td class="text-center fw-bolder stage-3">{{ engine.data.stage3_horsepower_total }} ch ({{ chToKw(engine.data.stage3_horsepower_total) }} kW)</td>
                                        <td class="text-center fw-bolder stage-3">{{ engine.data.stage3_torque_total }} Nm</td>
                                    </tr>
                                    </tbody>
                                </table>
                                <p class="fs-15">
                                    <i class="mdi mdi-arrow-right-thin fs-20 align-middle stage-3"></i> <span class=" fw-bold">{{ engine.data.stage3_price }} €</span> <span class="text-muted">TTC</span>
                                </p>
                                <template v-if="canViewNotes">
                                    <hr class="text-muted">
                                    <div class="engineers-notes">
                                        <div class="mt-4 mb-2">
                                            <span class="mb-2">Notes de l'ingénieur :<br></span>
                                            <div class="fst-italic text-body" v-html="getNote(engine.data.stage3_engineers_note)"></div>
                                        </div>
                                    </div>
                                    <hr class="text-muted">
                                    <chiptuning-internal-note
                                        stage="stage3"
                                        :note="engine.data.stage3_internal_note"
                                        :can-edit="canEditShiftech"
                                        :saving="savingEdit"
                                        @save="saveInternalNote"
                                    />
                                </template>
                            </div>
                        </div>
                        <!--E85-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'e85Tab' }" id="e85Tab" role="tabpanel" v-if="engine.data.e85_available">
                            <p class="text-body mb-3">
                                La conversion E85 Flex-Fuel offre à votre véhicule la flexibilité de rouler au Bioéthanol, à l'essence, ou à un mélange des deux, sans contrainte. Cette adaptation réduit de moitié le coût du carburant et diminue notablement les émissions de CO2, réduisant ainsi l'impact environnemental.
                            </p>
                            <div class="table-responsive">
                                <table class="table table-sm table-hover align-middle table-nowrap table-striped">
                                    <thead>
                                    <tr>
                                        <th scope="col" class="col-6"></th>
                                        <th scope="col" class="text-center col-3">PUISSANCE</th>
                                        <th scope="col" class="text-center col-3">COUPLE</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td class="fw-bold">Origine</td>
                                        <td class="text-center text-body">{{ engine.data.horsepower }} ch ({{ chToKw(engine.data.horsepower) }} kW)</td>
                                        <td class="text-center text-body">{{ engine.data.torque }} Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold text-dark">Gain</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;">-</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;">-</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold e-85">E85</td>
                                        <td class="text-center fw-bolder e-85">{{ engine.data.horsepower }} ch ({{ chToKw(engine.data.horsepower) }} kW)</td>
                                        <td class="text-center fw-bolder e-85">{{ engine.data.torque }} Nm</td>
                                    </tr>
                                    </tbody>
                                </table>
                                <p class="fs-15">
                                    <i class="mdi mdi-arrow-right-thin fs-20 align-middle e-85"></i> <span class=" fw-bold">{{ engine.data.e85_price }} €</span> <span class="text-muted">TTC</span>
                                </p>
                                <template v-if="canViewNotes">
                                    <hr class="text-muted">
                                    <div class="engineers-notes">
                                        <div class="mt-4 mb-2">
                                            <span class="mb-2">Notes de l'ingénieur :<br></span>
                                            <div class="fst-italic text-body" v-html="getNote(engine.data.e85_engineers_note)"></div>
                                        </div>
                                    </div>
                                    <hr class="text-muted">
                                    <chiptuning-internal-note
                                        stage="e85"
                                        :note="engine.data.e85_internal_note"
                                        :can-edit="canEditShiftech"
                                        :saving="savingEdit"
                                        @save="saveInternalNote"
                                    />
                                </template>
                            </div>
                        </div>
                        <!--E85+-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'e85+Tab' }" id="e85+Tab" role="tabpanel" v-if="engine.data.e85plus_available">
                            <p class="text-body mb-3">
                                La conversion E85+ Flex-Fuel permet à votre véhicule de fonctionner avec du Bioéthanol, de l'essence, ou un mélange des deux carburants, tout en bénéficiant de l’augmentation de puissance d’un stage 1. En plus de réduire le coût du carburant de moitié, elle diminue aussi les émissions de CO2, tout en améliorant significativement les performances.
                            </p>
                            <div class="table-responsive">
                                <table class="table table-sm table-hover align-middle table-nowrap table-striped">
                                    <thead>
                                    <tr>
                                        <th scope="col" class="col-6"></th>
                                        <th scope="col" class="text-center col-3">PUISSANCE</th>
                                        <th scope="col" class="text-center col-3">COUPLE</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td class="fw-bold">Origine</td>
                                        <td class="text-center text-body">{{ engine.data.horsepower }} ch ({{ chToKw(engine.data.horsepower) }} kW)</td>
                                        <td class="text-center text-body">{{ engine.data.torque }} Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold text-dark">Gain</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;"><span class="e-85plus fw-bold">+</span> {{ engine.data.e85plus_horsepower_gain }} ch (+{{ chToKw(engine.data.e85plus_horsepower_gain) }} kW)</td>
                                        <td class="text-center text-dark" style="white-space: nowrap;"><span class="e-85plus fw-bold">+</span> {{ engine.data.e85plus_torque_gain }}  Nm</td>
                                    </tr>
                                    <tr>
                                        <td class="fw-bold e-85plus">E85+</td>
                                        <td class="text-center fw-bolder e-85plus">{{ engine.data.e85plus_horsepower_total }} ch ({{ chToKw(engine.data.e85plus_horsepower_total) }} kW)</td>
                                        <td class="text-center fw-bolder e-85plus">{{ engine.data.e85plus_torque_total }} Nm</td>
                                    </tr>
                                    </tbody>
                                </table>
                                <p class="fs-15">
                                    <i class="mdi mdi-arrow-right-thin fs-20 align-middle e-85plus"></i> <span class=" fw-bold">{{ engine.data.e85plus_price }} €</span> <span class="text-muted">TTC</span>
                                </p>
                                <template v-if="canViewNotes">
                                    <hr class="text-muted">
                                    <div class="engineers-notes">
                                        <div class="mt-4 mb-2">
                                            <span class="mb-2">Notes de l'ingénieur :<br></span>
                                            <div class="fst-italic text-body" v-html="getNote(engine.data.e85plus_engineers_note)"></div>
                                        </div>
                                    </div>
                                    <hr class="text-muted">
                                    <chiptuning-internal-note
                                        stage="e85plus"
                                        :note="engine.data.e85plus_internal_note"
                                        :can-edit="canEditShiftech"
                                        :saving="savingEdit"
                                        @save="saveInternalNote"
                                    />
                                </template>
                            </div>
                        </div>
                        <!--ECU unlocked-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'ecuUnlocked' }" id="ecuUnlocked" role="tabpanel">
                            <p class="fs-15" v-if="engine.data.ecu_unlocked_price !== null">
                                <i class="mdi mdi-arrow-right-thin fs-20 align-middle"></i>
                                <span class=" fw-bold">{{ engine.data.ecu_unlocked_price }} €</span> <span class="text-muted">TTC</span>
                            </p>
                            <div class="">
                                <div class="mt-4 mb-2">
                                    <span class="mb-2"><strong>Notes ECU unlocked</strong><br></span>
                                    <div class="fst-italic text-body" v-html="engine.data.ecu_unlocked_note ?? '--'"></div>
                                </div>
                            </div>
                        </div>
                        <!--Gearbox-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'dsgTab' }" id="e85+Tab" role="tabpanel" v-if="engine.data.gearbox_tune_available">
                            <p class="text-body mb-3">
                                La reprogrammation des boîtes automatiques modernes permet d’optimiser leur performance en réduisant le temps de passage des rapports, en augmentant le régime et le couple maximaux, et en améliorant la réactivité globale.
                            </p>
                            <div>
                                <p class="fs-15">
                                    <i class="mdi mdi-arrow-right-thin fs-20 align-middle text-dark"></i>
                                    <span class=" fw-bold">
                                        {{ engine.data.gearbox_tune_price }} €</span> <span class="text-muted">TTC
                                </span>
                                </p>
                                <template v-if="canViewNotes">
                                    <hr class="text-muted">
                                    <div class="engineers-notes">
                                        <div class="mt-4 mb-2">
                                            <span class="mb-2">Notes de l'ingénieur :<br></span>
                                            <div class="fst-italic text-body" v-html="getNote(engine.data.gearbox_tune_engineers_note)"></div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <!--Options-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'options' }" id="options" role="tabpanel">
                            <ul v-if="engine.data.options && engine.data.options.length > 0">
                                <li class="mb-2" v-for="(option, indexOption) in sortOptions(engine.data.options)" :key="indexOption">
                                    <strong :title="option.description">{{ option.name }}</strong>
                                </li>
                            </ul>
                            <p class="text-muted mb-0" v-else>Aucune option.</p>
                        </div>
                        <!--ECUs-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'ecus' }" id="ecus" role="tabpanel">
                            <div class="table-responsive table-card mt-2">
                                <table class="table align-middle table-nowrap mb-0 table-hover">
                                    <thead class="table-light">
                                    <tr>
                                        <th class="col-3" scope="col">Catégorie</th>
                                        <th class="col-3" scope="col">Nom</th>
                                        <th class="col-3" scope="col">MCU</th>
                                        <th class="col-3 text-center" scope="col">Outils de lecture</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(ecu, indexEcu) in engine.data.ecus" :key="indexEcu">
                                        <td>{{ getCategoryLabel(ecu.category) }}</td>
                                        <td>{{ ecu.name }}</td>
                                        <td>{{ ecu.mcu ?? '--' }}</td>
                                        <td class="text-center">
                                            <a href="#" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm" @click="showEcuReadingTools(ecu)">Afficher</a>
                                        </td>
                                    </tr>
                                    <tr class="text-center font-italic text-muted" v-if="engine.data.ecus === null || engine.data.ecus?.length === 0">
                                        <td colspan="5">Aucun ECUs</td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <!--Charts-->
                        <div v-if="isShiftech" class="tab-pane" :class="{ 'active': activeTab === 'charts' }" id="charts" role="tabpanel">
                            <div v-if="canEdit" class="mb-3">
                                <button type="button" class="btn btn-sm btn-dark bg-gradient waves-effect waves-light" @click.prevent="showChartUpload = !showChartUpload">
                                    {{ showChartUpload ? 'Cacher le formulaire' : 'Ajouter une courbe' }}
                                </button>
                                <form v-show="showChartUpload" class="mt-3" @submit.prevent="submitCharts">
                                    <div class="row g-2 mb-2">
                                        <div class="col-md-4">
                                            <label class="form-label">Stage</label>
                                            <select v-model="chartStage" class="form-select">
                                                <option v-for="stage in CHART_STAGES" :key="stage.value" :value="stage.value">{{ stage.label }}</option>
                                            </select>
                                        </div>
                                        <div class="col-md-4 d-flex align-items-end">
                                            <span class="badge" :class="chartStageIsFull ? 'bg-danger-subtle text-danger' : 'bg-light text-muted'">
                                                {{ currentChartStageCount }} / {{ MAX_CHARTS_PER_STAGE }} pour ce stage
                                            </span>
                                        </div>
                                    </div>
                                    <form-input-file-pond
                                        name="chartFiles"
                                        label="Courbes"
                                        :errors="errors"
                                        :multiple="true"
                                        accepted="image/jpeg,image/png,image/webp"
                                        :reset-file-list="resetChartFiles"
                                        @update:field="chartFiles = $event"
                                    />
                                    <div class="d-flex justify-content-end">
                                        <button type="submit" class="btn btn-secondary" :disabled="savingEdit || !chartFiles.length || chartStageIsFull || tooManyCharts">Valider</button>
                                    </div>
                                    <div v-if="chartStageIsFull" class="form-text text-danger mt-1">
                                        Limite atteinte : {{ MAX_CHARTS_PER_STAGE }} courbes maximum pour ce stage. La suppression se fait depuis Shiftech-API.
                                    </div>
                                    <div v-else-if="tooManyCharts" class="form-text text-danger mt-1">
                                        {{ chartFiles.length }} fichier(s) sélectionné(s) pour {{ chartStageRemaining }} place(s) restante(s) sur ce stage.
                                    </div>
                                </form>
                            </div>

                            <!-- Cas où il n'y a pas de médias -->
                            <div v-if="!engine.data.dyno_charts || engine.data.dyno_charts.length === 0" class="text-center py-5">
                                <div class="mb-3">
                                    <i class="mdi mdi-chart-bell-curve fs-1 text-muted"></i>
                                </div>
                                <h5 class="text-muted">Aucune courbes disponible</h5>
                            </div>

                            <chiptuning-charts-gallery
                                v-else
                                :medias="engine.data.dyno_charts"
                            />
                        </div>
                        <!--Photos-->
                        <div v-if="isShiftech" class="tab-pane" :class="{ 'active': activeTab === 'photos' }" id="photos" role="tabpanel">

                            <div v-if="canEdit" class="mb-3">
                                <button type="button" class="btn btn-sm btn-dark bg-gradient waves-effect waves-light" @click.prevent="showPhotoUpload = !showPhotoUpload">
                                    {{ showPhotoUpload ? 'Cacher le formulaire' : 'Ajouter une photo' }}
                                </button>
                                <form v-show="showPhotoUpload" class="mt-3" @submit.prevent="submitPhotos">
                                    <p class="text-muted fs-12 mb-2">
                                        <i class="ri-information-line align-bottom"></i>
                                        Les photos sont au niveau de la version (partagées entre tous les moteurs de cette version) et visibles publiquement sur le site.
                                    </p>
                                    <form-input-file-pond
                                        name="photoFiles"
                                        label="Photos"
                                        :errors="errors"
                                        :multiple="true"
                                        accepted="image/jpeg,image/png,image/webp"
                                        :reset-file-list="resetPhotoFiles"
                                        @update:field="photoFiles = $event"
                                    />
                                    <div class="d-flex justify-content-end">
                                        <button type="submit" class="btn btn-secondary" :disabled="savingEdit || !photoFiles.length">Valider</button>
                                    </div>
                                </form>
                            </div>

                            <div v-if="!engine.data.photos || engine.data.photos.length === 0" class="text-center py-5">
                                <div class="mb-3">
                                    <i class="ri-image-2-line fs-1 text-muted"></i>
                                </div>
                                <h5 class="text-muted">Aucune photo disponible</h5>
                            </div>

                            <chiptuning-photos-gallery
                                v-else
                                :medias="engine.data.photos"
                            />

                        </div>
                        <!--Datalogs-->
                        <div v-if="canEditShiftech" class="tab-pane" :class="{ 'active': activeTab === 'datalogs' }" id="datalogs" role="tabpanel">
                            <p class="text-body mb-3">
                                Liens vers les datalogs (LOG) de reprogrammation de ce moteur, classés par stage.
                                <button type="button" class="mt-2 btn btn-sm btn-dark bg-gradient waves-effect waves-light d-block" @click.prevent="showDatalogUpload = !showDatalogUpload">
                                    {{ showDatalogUpload ? 'Cacher le formulaire' : 'Ajouter un datalog' }}
                                </button>
                            </p>

                            <form v-show="showDatalogUpload" class="mb-3" @submit.prevent="submitDatalogLink">
                                <div class="row g-2 mb-2">
                                    <div class="col-md-4">
                                        <label class="form-label">Stage</label>
                                        <select v-model="datalogStage" class="form-select">
                                            <option v-for="stage in DATALOG_STAGES" :key="stage.value" :value="stage.value">{{ stage.label }}</option>
                                        </select>
                                    </div>
                                    <div class="col-md-4 d-flex align-items-end">
                                        <span class="badge" :class="datalogStageIsFull ? 'bg-danger-subtle text-danger' : 'bg-light text-muted'">
                                            {{ currentDatalogStageCount }} / {{ MAX_DATALOGS_PER_STAGE }} pour ce stage
                                        </span>
                                    </div>
                                </div>

                                <label class="form-label">Lien du datalog (Autotuner, Tunezilla…)</label>
                                <div class="row g-2">
                                    <div class="col-md-5">
                                        <input v-model="datalogLinkName" type="text" class="form-control" placeholder="Nom (optionnel)">
                                    </div>
                                    <div class="col-md-7">
                                        <div class="input-group">
                                            <input v-model="datalogLinkUrl" type="url" class="form-control" placeholder="https://…">
                                            <button type="submit" class="btn btn-secondary" :disabled="savingEdit || !datalogLinkUrl || datalogStageIsFull">Ajouter le lien</button>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="datalogStageIsFull" class="form-text text-danger mt-1">
                                    Limite atteinte : {{ MAX_DATALOGS_PER_STAGE }} datalogs maximum pour ce stage. La suppression se fait depuis Shiftech-API.
                                </div>
                            </form>

                            <div v-if="!engine.data.datalogs || engine.data.datalogs.length === 0" class="text-center py-5">
                                <div class="mb-3">
                                    <i class="ri-file-list-3-line fs-1 text-muted"></i>
                                </div>
                                <h5 class="text-muted">Aucun datalog disponible</h5>
                            </div>

                            <div v-else class="vstack gap-3">
                                <div v-for="group in groupedDatalogs" :key="group.stage ?? 'unclassified'">
                                    <h6 class="text-uppercase text-muted fs-11 mb-2">
                                        {{ group.label }} <span class="text-body-tertiary">({{ group.items.length }})</span>
                                    </h6>

                                    <div class="vstack gap-2">
                                        <div class="border rounded border-dashed p-2" v-for="(datalog, index) in group.items" :key="index">
                                            <div class="d-flex align-items-center">
                                                <div class="flex-shrink-0 me-3">
                                                    <div class="avatar-sm">
                                                        <div class="avatar-title bg-light text-secondary rounded fs-24 shadow">
                                                            <i :class="datalog.type === 'link' ? 'ri-links-line' : 'ri-file-list-3-line'"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="flex-grow-1 overflow-hidden">
                                                    <h5 class="fs-13 mb-0">
                                                        <a :href="datalog.url" target="_blank" class="text-body text-truncate d-block">{{ datalog.name }}</a>
                                                    </h5>
                                                    <div class="d-flex gap-1 mt-1">
                                                        <span class="badge bg-light text-muted">{{ datalog.type === 'link' ? 'Lien' : 'Fichier' }}</span>
                                                        <span class="badge bg-light text-muted">{{ datalogStageLabel(datalog.stage) }}</span>
                                                    </div>
                                                </div>
                                                <div class="flex-shrink-0 ms-2">
                                                    <div class="d-flex gap-1">
                                                        <a :href="datalog.url" target="_blank" class="btn btn-icon text-muted btn-sm fs-18 shadow-none" :download="datalog.type !== 'link'">
                                                            <i :class="datalog.type === 'link' ? 'ri-external-link-line' : 'ri-download-2-line'"></i>
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!--Devis-->
                        <div class="tab-pane" :class="{ 'active': activeTab === 'dsg' }" id="quotes" role="tabpanel">
                            <p class="text-body mb-3">
                                Vous trouverez ci-dessous les devis qui ont déja été établis pour ce véhicule. <br>
                                <button type="button" class="mt-2 btn btn-sm btn-dark bg-gradient waves-effect waves-light" @click.prevent="showUploadAttachments = !showUploadAttachments" v-if="engine.can.update">{{ showUploadAttachments ? 'Cacher formulaire' : 'Ajouter devis' }}</button>
                            </p>
                            <loader v-if="loadingAttachments && showUploadAttachments"/>
                            <form v-on:submit.prevent="addAttachment" v-show="!loadingAttachments && showUploadAttachments">
                                <form-input-file-pond
                                    name="form.attachments"
                                    label="Devis"
                                    :label-hidden="false"
                                    :errors="errors"
                                    :required="false"
                                    :data="form.attachments"
                                    @update:field="form.attachments = $event"
                                    bs-class="col-md-12"
                                    :disabled="false"
                                    :multiple="true"
                                    :reset-file-list="resetFileList"
                                />
                                <div class="d-flex gap-2 justify-content-end mb-3">
                                    <button type="submit" class="btn btn-secondary" :disabled="!form.attachments.length > 0">Valider</button>
                                </div>
                            </form>

                            <attachment-files :attachments="engine.data.attachments" stacked downloadable :can-delete="engine.can.delete" @delete="(id) => deleteAttachment(id, engine.data.attachments.findIndex((attachment) => attachment.data.id === id))" />
                        </div>

                        <!--Model show message-->
                        <div class="modal fade zoomIn" id="modalShowReadingTools" tabindex="-1" aria-hidden="true">
                            <div class="modal-dialog modal-xl">
                                <div class="modal-content" v-if="ecuToShow !== null">
                                    <div class="modal-header">
                                        <h5 class="modal-title"><i class="mdi mdi-smart-card-reader-outline me-2"></i>Méthodes de lecture</h5>
                                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                    </div>
                                    <div class="modal-body p-4">
                                        <ul v-if="ecuToShow.reading_tools && ecuToShow.reading_tools.length > 0">
                                            <li class="mb-2" v-for="(readingTool, indexReadingTool) in ecuToShow.reading_tools" :key="indexReadingTool">
                                                {{ readingTool.name }}
                                            </li>
                                        </ul>
                                        <p class="text-muted mb-0" v-else>Aucun outil de lecture.</p>
                                        <div class="text-end mb-0">
                                            <button class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" id="deleteRecord-close" data-bs-dismiss="modal">Fermer</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!--Model show message-->
                        <div class="modal fade zoomIn" id="modalWarningMessage" tabindex="-1" aria-hidden="true">
                            <div class="modal-dialog modal-lg">
                                <div class="modal-content">
                                    <div class="modal-header">
                                        <h5 class="modal-title">
                                            <i class="mdi mdi-alert me-2"></i>Warning message
                                        </h5>
                                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                    </div>
                                    <div class="modal-body p-4">
                                        <p>
                                            {{ engine.data.warning_message }}
                                        </p>
                                        <div class="text-end mb-0">
                                            <button class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" id="deleteRecord-close" data-bs-dismiss="modal">Fermer</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
